import { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import { searchLayers, searchFeatures, MapLayerSearchHit, MapFeatureSearchHit } from "../../queries/maps";
import useNetworkHeaders from "../../../../hooks/useNetworkHeaders";

interface UseFeatureSearchOptions {
  initialSearch?: string;
  debounceMs?: number;
  onlyPolygonalFeatures?: boolean;
  includeDeactivated?: boolean;
  limit?: number;
}

export function useFeatureSearch({
  initialSearch = "",
  debounceMs = 300,
  onlyPolygonalFeatures = true,
  includeDeactivated = false,
  limit,
}: UseFeatureSearchOptions = {}) {
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [isSearching, setIsSearching] = useState(false);
  const [searchedLayers, setSearchedLayers] = useState<MapLayerSearchHit[]>([]);
  const [searchedFeatures, setSearchedFeatures] = useState<{ [layerId: string]: MapFeatureSearchHit[] }>({});
  const networkHeaders = useNetworkHeaders();

  // Search for layers
  const searchLayersRequest = useCallback(
    async (query: string) => {
      try {
        setIsSearching(true);

        const response = await searchLayers(networkHeaders)({
          q: query,
          ...(onlyPolygonalFeatures ? { hasPolygonalFeatures: onlyPolygonalFeatures } : {}),
          ...(limit ? { size: limit } : {}),
          includeDeactivated,
        });

        if (response && response.hits && response.hits.hits) {
          const layers = response.hits.hits.flatMap((hit) => hit._source).filter(Boolean);
          setSearchedLayers(layers);
        } else {
          setSearchedLayers([]);
        }
      } catch (error) {
        console.error("Error searching layers:", error);
        setSearchedLayers([]);
      } finally {
        setIsSearching(false);
      }
    },
    [networkHeaders],
  );

  // Search for features in a specific layer
  const searchFeaturesInLayer = useCallback(
    async (layerId: number, query: string) => {
      try {
        setIsSearching(true);

        const response = await searchFeatures(networkHeaders)({
          layerId,
          q: query,
          ...(onlyPolygonalFeatures ? { geometryType: ["Polygon", "MultiPolygon"] } : {}),
          ...(limit ? { size: limit } : {}),
        });

        if (response && response.hits && response.hits.hits) {
          const features: MapFeatureSearchHit[] = response.hits.hits.map((hit) => hit._source).filter(Boolean);

          setSearchedFeatures((prev) => ({
            ...prev,
            [layerId]: features,
          }));

          return features;
        }

        return [];
      } catch (error) {
        console.error(`Error searching features for layer ${layerId}:`, error);
        return [];
      } finally {
        setIsSearching(false);
      }
    },
    [networkHeaders],
  );

  // Search for features across all layers
  const searchAllFeatures = useCallback(
    async (query: string) => {
      try {
        setIsSearching(true);

        const response = await searchFeatures(networkHeaders)({
          q: query,
          ...(onlyPolygonalFeatures ? { geometryType: ["Polygon", "MultiPolygon"] } : {}),
          ...(limit ? { size: limit } : {}),
        });

        if (response && response.hits && response.hits.hits) {
          const features: MapFeatureSearchHit[] = response.hits.hits.map((hit) => hit._source).filter(Boolean);

          // Group features by layer
          const featuresByLayer: { [layerId: string]: MapFeatureSearchHit[] } = {};
          features.forEach((feature) => {
            if (feature.layer_id) {
              const layerId = feature.layer_id.toString();
              featuresByLayer[layerId] = featuresByLayer[layerId] || [];
              featuresByLayer[layerId].push(feature);
            }
          });

          setSearchedFeatures(featuresByLayer);
          return features;
        }

        return [];
      } catch (error) {
        console.error("Error searching all features:", error);
        return [];
      } finally {
        setIsSearching(false);
      }
    },
    [networkHeaders],
  );

  // Search for features by IDs
  const searchFeaturesById = useCallback(
    async (featureIds: string[], layerId?: number) => {
      try {
        setIsSearching(true);

        const response = await searchFeatures(networkHeaders)({
          id: featureIds,
          ...(layerId ? { layerId } : {}),
          ...(onlyPolygonalFeatures ? { geometryType: ["Polygon", "MultiPolygon"] } : {}),
          size: featureIds.length,
        });

        if (response && response.hits && response.hits.hits) {
          return response.hits.hits.map((hit) => hit._source).filter(Boolean);
        }

        return [];
      } catch (error) {
        console.error("Error searching features by IDs:", error);
        return [];
      } finally {
        setIsSearching(false);
      }
    },
    [networkHeaders],
  );

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      if (query.trim().length > 0) {
        searchLayersRequest(query);
        searchAllFeatures(query);
      } else {
        searchLayersRequest("");
        setSearchedFeatures({});
      }
    }, debounceMs),
    [searchLayersRequest, searchAllFeatures, debounceMs],
  );

  const updateSearchTerm = useCallback(
    (term: string) => {
      setSearchTerm(term);
      debouncedSearch(term);
    },
    [debouncedSearch],
  );

  useEffect(() => {
    searchLayersRequest("");
  }, []);

  return {
    searchTerm,
    updateSearchTerm,
    isSearching,
    searchedLayers,
    searchedFeatures,
    searchFeaturesInLayer,
    searchAllFeatures,
    searchFeaturesById,
  };
}
